import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Tag, Button, Space, Select, Input, Modal, Form,
  Row, Col, Card, Statistic, Typography, Tooltip, message,
  Drawer, Image, Timeline, Divider, Descriptions, Empty, Spin,
} from 'antd';
import {
  SearchOutlined, DownloadOutlined, ExclamationCircleOutlined,
  CheckCircleOutlined, ClockCircleOutlined, StarOutlined,
  EyeOutlined, EnvironmentOutlined, PhoneOutlined, UserOutlined,
} from '@ant-design/icons';
import { appealsApi, AppealListItem, AppealStats, AppealStatus, AppealDetail } from '../../api/appeals';

const { Title } = Typography;
const { Option } = Select;

// `submitted` и `resolved` — слова боевой базы (разнобой словарей DATA-02, см.
// backend/app/api/v1/appeals.py); показываем их как «Новое» и «Выполнено».
const STATUS_LABELS: Record<string, string> = {
  submitted: 'Новое',
  resolved: 'Выполнено',
  new: 'Новое',
  accepted: 'Принято',
  in_progress: 'В работе',
  done: 'Выполнено',
  rated: 'Оценено',
};

const STATUS_COLORS: Record<string, string> = {
  submitted: 'orange',
  resolved: 'green',
  new: 'orange',
  accepted: 'blue',
  in_progress: 'blue',
  done: 'green',
  rated: 'default',
};

const NEXT_STATUSES: Record<AppealStatus, AppealStatus | null> = {
  new: 'accepted',
  accepted: 'in_progress',
  in_progress: 'done',
  done: null,
  rated: null,
};

const AppealsList: React.FC = () => {
  const [appeals, setAppeals] = useState<AppealListItem[]>([]);
  const [stats, setStats] = useState<AppealStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [offset, setOffset] = useState(0);
  const [statusModal, setStatusModal] = useState<{ appeal: AppealListItem } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [detail, setDetail] = useState<AppealDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = useCallback(async (id: number) => {
    setDrawerId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await appealsApi.get(id);
      setDetail(res.data);
    } catch {
      message.error('Не удалось загрузить обращение');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const fetchAppeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await appealsApi.list({ search, status: statusFilter, limit: 50, offset });
      setAppeals(res.data);
    } catch {
      message.error('Ошибка загрузки обращений');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, offset]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await appealsApi.stats();
      setStats(res.data);
    } catch {
      // статистика некритична
    }
  }, []);

  useEffect(() => { fetchAppeals(); }, [fetchAppeals]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleExport = useCallback(async () => {
    try {
      const res = await appealsApi.export({ status: statusFilter });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `appeals_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Ошибка экспорта');
    }
  }, [statusFilter]);

  const handleStatusSubmit = async () => {
    if (!statusModal) return;
    const nextStatus = NEXT_STATUSES[statusModal.appeal.status];
    if (!nextStatus) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await appealsApi.updateStatus(statusModal.appeal.id, {
        status: nextStatus,
        comment: values.comment,
        department: values.department,
      });
      message.success('Статус обновлён');
      setStatusModal(null);
      form.resetFields();
      fetchAppeals();
      fetchStats();
    } catch (err: any) {
      if (err?.response?.data?.detail) {
        message.error(err.response.data.detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: '№',
      dataIndex: 'id',
      width: 70,
      render: (id: number) => (
        <Button type="link" size="small" onClick={() => openDetail(id)} style={{ padding: 0 }}>
          #{id}
        </Button>
      ),
    },
    {
      title: 'Заголовок',
      dataIndex: 'title',
      ellipsis: true,
      render: (t: string, record: AppealListItem) => (
        <a onClick={() => openDetail(record.id)}>{t}</a>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 130,
      render: (status: AppealStatus) => (
        <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
      ),
    },
    { title: 'Адрес', dataIndex: 'address', ellipsis: true },
    { title: 'Отдел', dataIndex: 'department', ellipsis: true },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      width: 110,
      render: (v: string) => new Date(v).toLocaleDateString('ru-RU'),
    },
    {
      title: 'Оценка',
      dataIndex: 'rating',
      width: 80,
      render: (r: number) => r ? `★ ${r}` : '—',
    },
    {
      title: 'Действие',
      width: 200,
      render: (_: unknown, record: AppealListItem) => {
        const next = NEXT_STATUSES[record.status];
        return (
          <Space size={4}>
            <Tooltip title="Подробности">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => openDetail(record.id)}
              />
            </Tooltip>
            {next && (
              <Button
                size="small"
                type="primary"
                onClick={() => setStatusModal({ appeal: record })}
              >
                → {STATUS_LABELS[next]}
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>Обращения граждан</Title>

      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card size="small">
              <Statistic title="Всего" value={stats.total} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="Новые" value={stats.by_status['new'] || 0}
                valueStyle={{ color: 'var(--l-accent)' }} prefix={<ExclamationCircleOutlined />} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="В работе" value={stats.by_status['in_progress'] || 0}
                valueStyle={{ color: 'var(--l-violet)' }} prefix={<ClockCircleOutlined />} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="Выполнено" value={stats.by_status['done'] || 0}
                valueStyle={{ color: 'var(--l-ok)' }} prefix={<CheckCircleOutlined />} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="Просрочено" value={stats.overdue_count}
                valueStyle={{ color: stats.overdue_count > 0 ? 'var(--l-danger)' : undefined }} />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic title="Средняя оценка" value={stats.avg_rating}
                prefix={<StarOutlined />} precision={1} />
            </Card>
          </Col>
        </Row>
      )}

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="Поиск по заголовку/адресу"
          prefix={<SearchOutlined />}
          style={{ width: 260 }}
          value={search}
          onChange={e => { setSearch(e.target.value); setOffset(0); }}
          allowClear
        />
        <Select
          placeholder="Статус"
          style={{ width: 160 }}
          allowClear
          value={statusFilter}
          onChange={v => { setStatusFilter(v); setOffset(0); }}
        >
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <Option key={k} value={k}>{v}</Option>
          ))}
        </Select>
        <Tooltip title="Экспорт в Excel">
          <Button icon={<DownloadOutlined />} onClick={handleExport}>Экспорт</Button>
        </Tooltip>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={appeals}
        loading={loading}
        size="small"
        pagination={{
          pageSize: 50,
          current: offset / 50 + 1,
          total: stats?.total ?? appeals.length,
          showTotal: (t) => `Всего: ${t}`,
          showSizeChanger: false,
          onChange: (page) => setOffset((page - 1) * 50),
        }}
      />

      <Modal
        open={!!statusModal}
        title={statusModal
          ? `Обращение #${statusModal.appeal.id} → ${STATUS_LABELS[NEXT_STATUSES[statusModal.appeal.status]!]}`
          : ''}
        onOk={handleStatusSubmit}
        onCancel={() => { setStatusModal(null); form.resetFields(); }}
        confirmLoading={submitting}
        okText="Подтвердить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          {statusModal?.appeal.status === 'new' && (
            <Form.Item name="department" label="Отдел (необязательно)">
              <Input placeholder="Напр. ЖКХ, Благоустройство..." />
            </Form.Item>
          )}
          <Form.Item name="comment" label="Комментарий (необязательно)">
            <Input.TextArea rows={3} placeholder="Причина изменения статуса..." />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        width={560}
        title={detail ? `Обращение #${detail.id}` : (drawerId ? `Обращение #${drawerId}` : '')}
        open={drawerId !== null}
        onClose={() => { setDrawerId(null); setDetail(null); }}
        extra={detail && NEXT_STATUSES[detail.status] && (
          <Button
            type="primary"
            onClick={() => {
              setStatusModal({ appeal: detail as unknown as AppealListItem });
            }}
          >
            → {STATUS_LABELS[NEXT_STATUSES[detail.status]!]}
          </Button>
        )}
      >
        {detailLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        )}
        {detail && (
          <>
            <Space style={{ marginBottom: 12 }}>
              <Tag color={STATUS_COLORS[detail.status]}>{STATUS_LABELS[detail.status]}</Tag>
              {detail.category?.name_ru && <Tag>{detail.category.name_ru}</Tag>}
              {detail.is_anonymous && <Tag color="default">Анонимно</Tag>}
            </Space>

            <Typography.Title level={5} style={{ marginTop: 0 }}>
              {detail.title}
            </Typography.Title>

            {(detail.description || detail.body) && (
              <Typography.Paragraph
                style={{ whiteSpace: 'pre-wrap', color: 'var(--l-text)', marginBottom: 16 }}
              >
                {detail.description || detail.body}
              </Typography.Paragraph>
            )}

            <Descriptions column={1} size="small" bordered>
              {detail.address && (
                <Descriptions.Item label={<><EnvironmentOutlined /> Адрес</>}>
                  {detail.address}
                  {detail.district ? ` · ${detail.district}` : ''}
                </Descriptions.Item>
              )}
              {!detail.is_anonymous && (
                <>
                  {detail.user_full_name && (
                    <Descriptions.Item label={<><UserOutlined /> Заявитель</>}>
                      {detail.user_full_name}
                    </Descriptions.Item>
                  )}
                  {detail.user_phone && (
                    <Descriptions.Item label={<><PhoneOutlined /> Телефон</>}>
                      <a href={`tel:${detail.user_phone}`}>{detail.user_phone}</a>
                    </Descriptions.Item>
                  )}
                </>
              )}
              {detail.department && (
                <Descriptions.Item label="Отдел">{detail.department}</Descriptions.Item>
              )}
              {detail.deadline && (
                <Descriptions.Item label="Дедлайн">
                  {new Date(detail.deadline).toLocaleString('ru-RU')}
                </Descriptions.Item>
              )}
              {detail.rating != null && (
                <Descriptions.Item label="Оценка">
                  ★ {detail.rating}{detail.rating_comment ? ` — ${detail.rating_comment}` : ''}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Создано">
                {new Date(detail.created_at).toLocaleString('ru-RU')}
              </Descriptions.Item>
            </Descriptions>

            {(detail.media?.length ?? 0) > 0 && (
              <>
                <Divider orientation="left" plain>Фото ({detail.media!.length})</Divider>
                <Image.PreviewGroup>
                  <Space wrap>
                    {detail.media!.map((m) => (
                      <Image
                        key={m.id}
                        src={m.thumbnail_url || m.url}
                        width={96}
                        height={96}
                        style={{ objectFit: 'cover', borderRadius: 8 }}
                        preview={{ src: m.url }}
                      />
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </>
            )}

            {(detail.status_log?.length ?? 0) > 0 && (
              <>
                <Divider orientation="left" plain>История статусов</Divider>
                <Timeline
                  items={detail.status_log!.map((e) => ({
                    color: STATUS_COLORS[e.to_status] === 'green' ? 'green'
                      : STATUS_COLORS[e.to_status] === 'red' ? 'red'
                      : STATUS_COLORS[e.to_status] === 'blue' ? 'blue' : 'gray',
                    children: (
                      <>
                        <div>
                          {e.from_status ? (
                            <Tag>{STATUS_LABELS[e.from_status]}</Tag>
                          ) : null}
                          {e.from_status && '→ '}
                          <Tag color={STATUS_COLORS[e.to_status]}>{STATUS_LABELS[e.to_status]}</Tag>
                        </div>
                        {e.comment && (
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {e.comment}
                          </Typography.Text>
                        )}
                        <div style={{ fontSize: 11, color: 'var(--l-faint)', marginTop: 4 }}>
                          {e.changed_by_full_name || 'Система'} · {new Date(e.created_at).toLocaleString('ru-RU')}
                        </div>
                      </>
                    ),
                  }))}
                />
              </>
            )}

            {!(detail.status_log?.length) && !(detail.media?.length) && (
              <Empty
                style={{ marginTop: 24 }}
                description="Нет фото и истории статусов"
              />
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default AppealsList;
